import React from "react";
import { Box, TextField, Typography, Stack, Button, Skeleton, Card, CardHeader } from "@mui/material";
import { useAgents, useAgentsByList, useContacts } from "../api";
import { BeatLoader } from "react-spinners";
import styled from "@emotion/styled";
import { limitString } from "../../../../../util";
import ErrorHandler from "../error-handling/ErrorHandler";
import { AxiosError } from "axios";
import { COLORS } from "../../../../../Theme";

export default function AgentsViewer({
  list,
  selectedAgent,
  onSelectAgent,
}: {
  list: List | undefined;
  selectedAgent: Agent | undefined;
  onSelectAgent: (v: Agent) => void;
}) {
  const {
    data: agents,
    isLoading: isLoadingAgents,
    error,
    refetch,
  } = useAgentsByList({
    list_id: list?.id || "",
  });

  return (
    <Box
      sx={{ background: "#fff", borderRadius: 1, padding: 3, paddingTop: 2 }}
    >
      <Stack gap={3}>
        <Stack>
          <Typography style={{ fontWeight: "bold", fontSize: 16 }}>
            Call Center Agents
          </Typography>
          {!isLoadingAgents && agents?.length === 0 && (
            <Typography
              style={{
                fontSize: 14,
                fontWeight: "bold",
                color: "gray",
                paddingTop: 5,
              }}
            >
              No active Agents on this list
            </Typography>
          )}
        </Stack>
        {isLoadingAgents ? (
          <AgentsLoader />
        ) : error ? (
          <Stack padding={2}>
            <ErrorHandler error={error as AxiosError} onRetry={refetch} />
          </Stack>
        ) : (
          agents && (
            <Stack flexDirection="row" gap={2} flexWrap="wrap">
              {agents.map((agent) => (
                <AgentItem
                  agent={agent}
                  isSelected={selectedAgent?.id === agent.id}
                  onClick={onSelectAgent}
                />
              ))}
            </Stack>
          )
        )}
      </Stack>
    </Box>
  );
}

const AgentItem = ({ agent, onClick, isSelected }: { agent: Agent; onClick: (v: Agent) => void; isSelected: boolean; }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${isSelected ? "#4a90e2" : "#dedede"}`,
        borderRadius: 2,
        padding: 1.5,
        paddingRight: 2,
        width: "33%",
        cursor: "pointer",
      }}
      onClick={() => onClick(agent)}
    >
      <Stack flexDirection="row" gap={2} alignItems="center">
        <AgentCircle color={agent.color || ""}>
          <Typography style={{ color: "#fff", fontWeight: "bold" }}>
            {(() => {
              const [namePart] = agent.email.split("@");
              const parts = namePart.split(".");
              if (parts.length === 1) return parts[0][0].toUpperCase();
              return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
            })()}
          </Typography>
        </AgentCircle>
        <Stack>
          <Typography style={{ fontWeight: "bold" }}>
            {limitString(agent.email, 24)}
          </Typography>
          <Typography style={{ fontSize: 14, color: "gray" }}>
            {agent?.lead_count} Active Customers
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const AgentCircle = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  background: ${({ color }) => color};
  justify-content: center;
  align-items: center;
  border-radius: 50px;
`;

const AgentSkeleton = () => {
  return (
    <Card
      sx={{
        width: "35%",
        borderRadius: 2,
        background: "#333333",
        backgroundImage: "linear-gradient(to right, #ededed, #ededed)",
        backgroundColor: "transparent", // Override MUI default
        boxShadow: "none", // Optional: remove card shadow if needed
      }}
    >
      <CardHeader
        avatar={
          <Skeleton
            animation="wave"
            variant="circular"
            width={40}
            height={40}
          />
        }
        title={
          <Skeleton
            animation="wave"
            height={10}
            width="80%"
            style={{ marginBottom: 6 }}
          />
        }
        subheader={
          <Skeleton
            animation="wave"
            height={10}
            width="40%"
          />
        }
      />
    </Card>
  );
};

const AgentsLoader = () => {
  return (
    <Stack flexDirection="row" gap={2} flexWrap="wrap">
      <AgentSkeleton />
      <AgentSkeleton />
      <AgentSkeleton />
    </Stack>
  )
};